import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { TimeTag, DayOfWeek } from './TimeTagSettingsModal';

// TimeTag는 TimeTagSettingsModal에서 import하므로 별도 정의 불필요

interface TimeTagSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableTimeTags: TimeTag[];
  selectedTimeTag: TimeTag | null;
  selectedDayOfWeek: string;
  onTimeTagSelect: (timeTag: TimeTag | null) => void;
  onDayOfWeekChange: (dayOfWeek: string) => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 20px;
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 56px);
  z-index: 10000;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border: var(--modal-shell-border);
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  position: relative;
  z-index: 10001;
  box-shadow: var(--modal-shadow-large);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--color-border);
  
  h3 {
    margin: 0;
    color: var(--color-accent-text);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--color-text-gray);
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: var(--color-text-primary);
  }
`;

const TimeTagSelectBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const NoTimeTagButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: var(--color-panel);
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 16px;
  margin-bottom: 16px;
  
  &:hover {
    background: var(--color-border);
    border-color: var(--color-text-muted);
  }
`;

const DayTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  padding: 4px;
  background: var(--color-panel);
  border-radius: 8px;
  border: 2px solid var(--color-border);
`;

const DayTabRow = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-wrap: nowrap;
`;

const DayTab = styled.button<{ active: boolean; $fullWidth?: boolean }>`
  flex: ${props => props.$fullWidth ? '1' : '1'};
  padding: 8px 12px;
  border: none;
  background: ${props => props.active ? '#10504A' : 'transparent'};
  color: ${props => props.active ? 'white' : '#6c757d'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 0;
  min-height: 36px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  /* 다크모드: 선택한 탭의 텍스트를 흰색으로 (이미 white이지만 명시적으로) */
  /* 다크모드: 활성화 상태일 때 초록색 대신 회색 배경 사용 */
  :root:not([data-theme='light']) & {
    color: ${props => props.active ? '#ffffff' : '#6c757d'};
    background: ${props => props.active ? '#505050' : 'transparent'} !important;
  }

  &:hover {
    background: ${props => props.active ? '#0D4039' : 'var(--color-border)'};
  }
  
  /* 다크모드: 호버 시 배경색을 회색으로 */
  :root:not([data-theme='light']) &:hover {
    background: ${props => props.active ? '#606060' : 'var(--color-border)'} !important;
  }
  
  @media (max-width: 480px) {
    min-width: 0;
    min-height: 40px;
    font-size: 13px;
    padding: 6px 8px;
  }
  
  @media (max-width: 360px) {
    font-size: 12px;
    padding: 4px 6px;
    min-height: 36px;
  }
`;

const TimeTagList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TimeTagItem = styled.div<{ selected: boolean }>`
  padding: 12px 16px;
  border: 1px solid ${props => props.selected ? '#10504A' : 'var(--color-border)'};
  border-radius: 8px;
  background: ${props => props.selected ? 'var(--color-panel)' : 'var(--color-surface)'};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    border-color: ${props => props.selected ? '#0D4039' : '#10504A'};
    background: ${props => props.selected ? 'var(--color-panel)' : 'var(--color-panel)'};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  /* 다크모드: 선택된 항목의 테두리 색상을 회색으로 */
  :root:not([data-theme='light']) & {
    border: 1px solid ${props => props.selected ? '#505050' : 'var(--color-border)'} !important;
  }
  
  /* 다크모드: 호버 시 테두리 색상을 회색으로 */
  :root:not([data-theme='light']) &:hover {
    border-color: ${props => props.selected ? '#606060' : '#505050'} !important;
  }
`;

const TimeTagName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 420px) {
    font-size: clamp(12px, 3vw, 16px);
  }
`;

const TimeTagTime = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-gray);
  margin: 0;
  padding: 0;
  line-height: 1;
  
  @media (max-width: 420px) {
    font-size: clamp(10px, 2.5vw, 14px);
  }
`;

export const TimeTagSelectModal: React.FC<TimeTagSelectModalProps> = ({
  isOpen,
  onClose,
  availableTimeTags,
  selectedTimeTag,
  selectedDayOfWeek,
  onTimeTagSelect,
  onDayOfWeekChange
}) => {
  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const dayOfWeeks = ['일', '월', '화', '수', '목', '금', '토'];
  
  const filteredTimeTags = availableTimeTags.filter(tag => {
    const days = Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek : [tag.dayOfWeek];
    // 하위 호환: '일반'은 모든 요일에서 표시
    if (days.includes('일반' as any)) return true;
    return days.includes(selectedDayOfWeek as DayOfWeek);
  });

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>타임 태그 선택</h3>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <TimeTagSelectBody>
          {/* 타임 태그 없음 버튼 */}
          <NoTimeTagButton
            onClick={() => {
              onTimeTagSelect(null);
              onClose();
            }}
          >
            타임 태그 선택 초기화
          </NoTimeTagButton>

          {/* 요일별 탭 */}
          <DayTabs>
            <DayTabRow>
              {dayOfWeeks.map(day => (
                <DayTab
                  key={day}
                  active={selectedDayOfWeek === day}
                  onClick={() => onDayOfWeekChange(day)}
                >
                  {day}
                </DayTab>
              ))}
            </DayTabRow>
          </DayTabs>

          {/* 타임 태그 목록 (전체 요일 선택 태그는 해당 요일 탭에서도 표시됨) */}
          <TimeTagList>
            {filteredTimeTags.map(tag => (
              <TimeTagItem
                key={tag.id}
                selected={selectedTimeTag?.id === tag.id}
                onClick={() => {
                  onTimeTagSelect(tag);
                  onClose();
                }}
              >
                <TimeTagName>{tag.name}</TimeTagName>
                <TimeTagTime>{tag.startTime} ~ {tag.endTime}</TimeTagTime>
              </TimeTagItem>
            ))}
          </TimeTagList>
        </TimeTagSelectBody>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};